import { useCallback, useEffect, useRef, useState } from "react";
import { EonUiButton } from "@eon-ui/eon-ui-components-react";
import { useJwtToken, useUserData } from "@me8eon/react_authentication";
import useAidwopsStore from "./AidwopsStore";
import { api } from "./expertApi";
import type {
    AskContext,
    GraphResponse,
    InsightCard,
    Persona,
} from "./aidwops.types";
import { SearchLanding } from "./expert-dashboard/SearchLanding";
import { type AidwopsView, PersonaRail } from "./expert-dashboard/PersonaRail";
import { InsightCards } from "./expert-dashboard/InsightCards";
import { TopActions } from "./expert-dashboard/TopActions";
import { PostureCharts } from "./expert-dashboard/PostureCharts";
import { DataTables } from "./expert-dashboard/DataTables";
import {
    ServiceGraphMini,
    ServiceGraphFull,
} from "./expert-dashboard/ServiceGraph";
import { AidwopsAiAssistant } from "./AidwopsAiAssistant";
import type {
    QueueRole,
    QueueStatusFilter,
} from "./expert-dashboard/ExpertQueueFilters";
import type { ChartSeries, DataTable } from "./aidwops.types";
import "./AidwopsDashboard.scss";

const EMPTY_GRAPH: GraphResponse = { nodes: [], edges: [] };

/** Sent with the first question from the landing page too, so queue commands ("tell me about #1") work from the first message. */
const QUEUE_ASK_CONTEXT: AskContext = { view: "queue", selected: null };
type DashboardSection = "insights" | "actions" | "graph" | "charts" | "tables";

type PersonaDashboardData = {
    insights: InsightCard[];
    actions: InsightCard[];
    graph: GraphResponse;
    charts: ChartSeries[];
    tables: DataTable[];
    loading: Record<DashboardSection, boolean>;
    loaded: Record<DashboardSection, boolean>;
    error: string | null;
};

type DashboardCache = Record<Persona, PersonaDashboardData>;

const PERSONAS: Persona[] = ["all", "zscaler", "intune"];
const SECTIONS: DashboardSection[] = [
    "insights",
    "actions",
    "graph",
    "charts",
    "tables",
];

function createPersonaData(): PersonaDashboardData {
    return {
        insights: [],
        actions: [],
        graph: EMPTY_GRAPH,
        charts: [],
        tables: [],
        loading: {
            insights: false,
            actions: false,
            graph: false,
            charts: false,
            tables: false,
        },
        loaded: {
            insights: false,
            actions: false,
            graph: false,
            charts: false,
            tables: false,
        },
        error: null,
    };
}

function createDashboardCache(): DashboardCache {
    return PERSONAS.reduce((cache, persona) => {
        cache[persona] = createPersonaData();
        return cache;
    }, {} as DashboardCache);
}

function createRequestIds() {
    return PERSONAS.reduce(
        (result, persona) => {
            result[persona] = {
                insights: 0,
                actions: 0,
                graph: 0,
                charts: 0,
                tables: 0,
            };
            return result;
        },
        {} as Record<Persona, Record<DashboardSection, number>>
    );
}

function fetchDashboardSection(
    section: "insights",
    persona: Persona,
    accessToken: string
): Promise<InsightCard[]>;
function fetchDashboardSection(
    section: "actions",
    persona: Persona,
    accessToken: string
): Promise<InsightCard[]>;
function fetchDashboardSection(
    section: "graph",
    persona: Persona,
    accessToken: string
): Promise<GraphResponse>;
function fetchDashboardSection(
    section: "charts",
    persona: Persona,
    accessToken: string
): Promise<ChartSeries[]>;
function fetchDashboardSection(
    section: "tables",
    persona: Persona,
    accessToken: string
): Promise<DataTable[]>;
function fetchDashboardSection(
    section: DashboardSection,
    persona: Persona,
    accessToken: string
): Promise<InsightCard[] | GraphResponse | ChartSeries[] | DataTable[]>;
function fetchDashboardSection(
    section: DashboardSection,
    persona: Persona,
    accessToken: string
) {
    switch (section) {
        case "insights":
            return api.getInsights(persona, accessToken);
        case "actions":
            return api.getActions(persona, accessToken);
        case "graph":
            return api.getGraph(persona, accessToken);
        case "charts":
            return api.getCharts(persona, accessToken);
        case "tables":
            return api.getTables(persona, accessToken);
        default:
            throw new Error(`Unsupported dashboard section: ${section}`);
    }
}

export function AidwopsDashboard() {
    const { loggedIn } = useUserData();
    const jwtToken = useJwtToken();
    const fetchInitialPrompts = useAidwopsStore(
        (state) => state.fetchInitialPrompts
    );
    const clearInitialPrompts = useAidwopsStore(
        (state) => state.clearInitialPrompts
    );
    const fetchQueue = useAidwopsStore((state) => state.fetchQueue);
    const clearQueue = useAidwopsStore((state) => state.clearQueue);
    const setQueueError = useAidwopsStore((state) => state.setQueueError);
    const queue = useAidwopsStore((state) => state.queue);
    const storedQueueScope = useAidwopsStore((state) => state.queueScope);
    const queueError = useAidwopsStore((state) => state.queueError);
    const messageCount = useAidwopsStore((state) => state.messages.length);
    const setSelectedItem = useAidwopsStore((state) => state.setSelectedItem);
    const appendMessageIfEmpty = useAidwopsStore(
        (state) => state.appendMessageIfEmpty
    );
    const resetChat = useAidwopsStore((state) => state.resetChat);
    const [view, setView] = useState<AidwopsView>("all");
    const [queueRole, setQueueRole] = useState<QueueRole>("engineer");
    const [queuePersonaFilter, setQueuePersonaFilter] =
        useState<Persona>("all");
    const [queueStatusFilter, setQueueStatusFilter] =
        useState<QueueStatusFilter>("pending");
    const [graphFilter, setGraphFilter] = useState<Persona | "degraded">("all");
    const [assistantQuestion, setAssistantQuestion] = useState<
        string | undefined
    >(undefined);
    const [dashboardCache, setDashboardCache] =
        useState<DashboardCache>(createDashboardCache);
    const requestIdsRef = useRef(createRequestIds());
    const activeRequestsRef = useRef<Record<string, boolean>>({});

    // Which persona's queue this view shows (null when it is not a queue view, or the ISO role is picked:
    // the ISO queue has its own data and does not use the engineer HITL queue the store loads).
    const queueScope: Persona | null =
        view === "expert-queue" && queueRole === "engineer"
            ? queuePersonaFilter
            : null;
    // The persona whose dashboard data (insights, charts, tables) is shown. Queue views use the
    // "all" data that is already loaded, so opening the Expert Queue never starts the Intune Graph calls.
    const persona: Persona =
        view === "all" || view === "zscaler" || view === "intune"
            ? view
            : "all";
    // The persona the store's queue is for: a queue view's own, otherwise the dashboard's (for the banner on the landing page).
    const storePersona: Persona = queueScope ?? persona;
    const currentData = dashboardCache[persona];

    const getAccessToken = useCallback(async () => {
        const accessToken = await jwtToken();
        if (!accessToken) {
            throw new Error("Unable to acquire access token for expert API");
        }
        return accessToken;
    }, [jwtToken]);

    // A chat that opened with another persona's queue cannot carry over into this one.
    const resetChatIfScopeChanged = (nextScope: Persona | null) => {
        const { messages } = useAidwopsStore.getState();
        if (
            nextScope &&
            messages.some(
                (message) =>
                    message.queueScope && message.queueScope !== nextScope
            )
        ) {
            resetChat();
        }
    };

    const enterView = (nextView: AidwopsView) => {
        const nextScope =
            nextView === "expert-queue" && queueRole === "engineer"
                ? queuePersonaFilter
                : null;
        resetChatIfScopeChanged(nextScope);
        setView(nextView);
    };

    const changeQueueRole = (nextRole: QueueRole) => {
        const nextScope = nextRole === "engineer" ? queuePersonaFilter : null;
        if (nextScope) {
            resetChatIfScopeChanged(nextScope);
        } else {
            // Switching to the ISO role: a leftover engineer queue message must not keep showing
            // alongside the ISO queue, so the chat always shows just the current role's queue.
            const { messages } = useAidwopsStore.getState();
            if (messages.some((message) => message.queueScope)) {
                resetChat();
            }
        }
        setQueueRole(nextRole);
    };

    const changeQueuePersonaFilter = (nextPersonaFilter: Persona) => {
        if (queueRole === "engineer") {
            resetChatIfScopeChanged(nextPersonaFilter);
        }
        setQueuePersonaFilter(nextPersonaFilter);
    };

    const handleQueueBannerClick = () => {
        resetChat();
        // The queue message is added again as soon as the (already loaded) queue is there; stay in the queue view the person is in.
        enterView(queueScope ? view : "expert-queue");
    };

    const handleOpenExpertQueue = async () => {
        handleQueueBannerClick();
        fetchQueue(storePersona, await getAccessToken());
    };

    const handleActionClick = (action: InsightCard) => {
        if (!action.queueId) {
            return;
        }

        setQueueRole("engineer");
        setQueuePersonaFilter(action.persona === "intune" ? "intune" : "all");
        enterView("expert-queue");
        setSelectedItem(action.queueId);
    };

    const handleQueueRetry = () => {
        void (async () => {
            try {
                await fetchQueue(storePersona, await getAccessToken());
            } catch (error) {
                setQueueError(
                    error instanceof Error ? error.message : String(error)
                );
            }
        })();
    };

    // One place loads the queue: whenever the persona it is needed for changes. A queue view then opens with it (next effect).
    useEffect(() => {
        if (!loggedIn) {
            clearInitialPrompts();
            clearQueue();
            return;
        }

        let isCurrent = true;
        const loadAidwopsData = async () => {
            try {
                const accessToken = await getAccessToken();
                if (!isCurrent) return;

                await Promise.all([
                    fetchInitialPrompts(storePersona, accessToken),
                    fetchQueue(storePersona, accessToken),
                ]);
            } catch (error) {
                if (!isCurrent) return;
                clearInitialPrompts();
                clearQueue();
                setQueueError(
                    error instanceof Error ? error.message : String(error)
                );
            }
        };

        void loadAidwopsData();
        return () => {
            isCurrent = false;
        };
        // getAccessToken depends on useJwtToken's unstable function identity; auth readiness is loggedIn.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        clearInitialPrompts,
        clearQueue,
        fetchInitialPrompts,
        fetchQueue,
        loggedIn,
        setQueueError,
        storePersona,
    ]);

    // A queue view opens with the assistant's queue message, once the queue of its persona has loaded.
    useEffect(() => {
        if (
            !queueScope ||
            !queue ||
            storedQueueScope !== queueScope ||
            messageCount > 0
        ) {
            return;
        }

        appendMessageIfEmpty({
            id: crypto.randomUUID(),
            role: "assistant",
            content: queue.summary.headline,
            response_id: null,
            raw_output: [],
            handledBy: "queue",
            queueScope,
            ui: {
                type: "queue",
                summary: queue.summary,
                items: queue.items,
            },
        });
    }, [
        appendMessageIfEmpty,
        messageCount,
        queue,
        queueScope,
        storedQueueScope,
    ]);

    useEffect(() => {
        const sectionsToFetch = SECTIONS.filter((section) => {
            const requestKey = `${persona}:${section}`;
            return (
                !currentData.loaded[section] &&
                !activeRequestsRef.current[requestKey]
            );
        });

        if (sectionsToFetch.length === 0) {
            return;
        }

        const sectionFlags = sectionsToFetch.reduce(
            (flags, section) => {
                flags[section] = true;
                return flags;
            },
            {} as Partial<Record<DashboardSection, boolean>>
        );

        sectionsToFetch.forEach((section) => {
            activeRequestsRef.current[`${persona}:${section}`] = true;
        });

        setDashboardCache((cache) => ({
            ...cache,
            [persona]: {
                ...cache[persona],
                error: null,
                loading: {
                    ...cache[persona].loading,
                    ...sectionFlags,
                },
            },
        }));

        getAccessToken()
            .then((accessToken) => {
                sectionsToFetch.forEach((section) => {
                    const requestKey = `${persona}:${section}`;
                    const requestId =
                        requestIdsRef.current[persona][section] + 1;
                    requestIdsRef.current[persona][section] = requestId;

                    fetchDashboardSection(section, persona, accessToken)
                        .then((data) => {
                            if (
                                requestIdsRef.current[persona][section] !==
                                requestId
                            )
                                return;
                            setDashboardCache((cache) => ({
                                ...cache,
                                [persona]: {
                                    ...cache[persona],
                                    [section]: data,
                                    error: null,
                                    loaded: {
                                        ...cache[persona].loaded,
                                        [section]: true,
                                    },
                                },
                            }));
                        })
                        .catch((err) => {
                            if (
                                requestIdsRef.current[persona][section] !==
                                requestId
                            )
                                return;
                            setDashboardCache((cache) => ({
                                ...cache,
                                [persona]: {
                                    ...cache[persona],
                                    error: String(err),
                                },
                            }));
                        })
                        .finally(() => {
                            if (
                                requestIdsRef.current[persona][section] !==
                                requestId
                            )
                                return;
                            delete activeRequestsRef.current[requestKey];
                            setDashboardCache((cache) => ({
                                ...cache,
                                [persona]: {
                                    ...cache[persona],
                                    loading: {
                                        ...cache[persona].loading,
                                        [section]: false,
                                    },
                                },
                            }));
                        });
                });
            })
            .catch((err) => {
                sectionsToFetch.forEach((section) => {
                    delete activeRequestsRef.current[`${persona}:${section}`];
                });
                setDashboardCache((cache) => ({
                    ...cache,
                    [persona]: {
                        ...cache[persona],
                        error: String(err),
                        loading: {
                            ...cache[persona].loading,
                            ...sectionsToFetch.reduce(
                                (flags, section) => {
                                    flags[section] = false;
                                    return flags;
                                },
                                {} as Partial<Record<DashboardSection, boolean>>
                            ),
                        },
                    },
                }));
            });

        return undefined;
    }, [currentData.loaded, getAccessToken, persona]);

    const filteredGraph: GraphResponse =
        graphFilter === "all"
            ? currentData.graph
            : graphFilter === "degraded"
              ? {
                    nodes: currentData.graph.nodes.filter(
                        (n) => n.status === "degraded"
                    ),
                    edges: [],
                }
              : {
                    nodes: currentData.graph.nodes.filter(
                        (n) => n.persona === graphFilter || n.persona === "all"
                    ),
                    edges: currentData.graph.edges.filter((e) => {
                        const from = currentData.graph.nodes.find(
                            (n) => n.id === e.from
                        );
                        const to = currentData.graph.nodes.find(
                            (n) => n.id === e.to
                        );
                        return (
                            (from?.persona === graphFilter ||
                                from?.persona === "all") &&
                            (to?.persona === graphFilter ||
                                to?.persona === "all")
                        );
                    }),
                };

    return (
        <div className="page aidwops-page">
            <div className="dash">
                <PersonaRail active={view} onChange={enterView} />
                <main className="main">
                    {queueScope && queueError && (
                        <div
                            className="card"
                            role="alert"
                            style={{
                                borderColor: "var(--eon-red)",
                                color: "var(--eon-red)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 12,
                            }}
                        >
                            <span>
                                Couldn't load expert queue: {queueError}
                            </span>
                            <EonUiButton
                                text="Retry"
                                rank="secondary"
                                size="small"
                                onClick={handleQueueRetry}
                            />
                        </div>
                    )}
                    {view === "expert-queue" ? (
                        <AidwopsAiAssistant
                            initialQuestion={assistantQuestion}
                            onInitialQuestionHandled={() =>
                                setAssistantQuestion(undefined)
                            }
                            onQueueBannerClick={handleQueueBannerClick}
                            onOpenExpertQueue={handleOpenExpertQueue}
                            isQueueView
                            queuePersona={queueScope ?? queuePersonaFilter}
                            queueStatus={queueStatusFilter}
                            queueRole={queueRole}
                            onQueueRoleChange={changeQueueRole}
                            onQueuePersonaChange={changeQueuePersonaFilter}
                            onQueueStatusChange={setQueueStatusFilter}
                        />
                    ) : (
                        <>
                            {view === "all" && (
                                <SearchLanding
                                    onQueueBannerClick={handleQueueBannerClick}
                                    onSubmit={() => enterView("expert-queue")}
                                    context={QUEUE_ASK_CONTEXT}
                                />
                            )}
                            {view !== "all" && (
                                <div style={{ height: "16px" }} />
                            )}
                            {currentData.error && (
                                <div
                                    className="card"
                                    style={{
                                        borderColor: "var(--eon-red)",
                                        color: "var(--eon-red)",
                                    }}
                                >
                                    Couldn't load dashboard data:{" "}
                                    {currentData.error}
                                </div>
                            )}
                            {view === "graph" ? (
                                <ServiceGraphFull
                                    graph={filteredGraph}
                                    filter={graphFilter}
                                    onFilterChange={setGraphFilter}
                                    loading={currentData.loading.graph}
                                />
                            ) : (
                                <>
                                    <InsightCards
                                        cards={currentData.insights.filter(
                                            (c) => c.source !== "expert_queue"
                                        )}
                                        loading={currentData.loading.insights}
                                    />
                                    <PostureCharts
                                        charts={currentData.charts}
                                        loading={currentData.loading.charts}
                                    />
                                    <section className="row-2col">
                                        <TopActions
                                            actions={currentData.actions.filter(
                                                (a) => a.queueId
                                            )}
                                            loading={
                                                currentData.loading.actions
                                            }
                                            onActionClick={handleActionClick}
                                        />
                                        <ServiceGraphMini
                                            graph={currentData.graph}
                                            loading={currentData.loading.graph}
                                        />
                                    </section>
                                    <DataTables
                                        tables={currentData.tables}
                                        loading={currentData.loading.tables}
                                    />
                                </>
                            )}
                        </>
                    )}
                </main>
            </div>
        </div>
    );
}
